import {
    BoltOutlined,
    BusinessOutlined,
    CreditCardOutlined,
    DescriptionOutlined,
    LocalOfferOutlined,
    PaymentsOutlined,
} from '@mui/icons-material';
import {ReactNode} from 'react';
import {FormattedMessage} from 'react-intl';
import {ContractStatusEnum, UserTypeEnum} from 'src/enums';
import {UserDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {UserFact} from './UserIdentityCard/UserIdentityCard.types';
import {LinkedRow} from './UserLinkedRail/UserLinkedRail.types';

/**
 * What a page shows about a person depends on which kind of person they are.
 *
 * Nine user types share this page, and they do not own the same things: a driver has a charger, a
 * plan and a contract; an employee has a company and a payment mode; a sales manager has none of
 * that and never will. The old page branched three ways and sent the other six types down the
 * driver's branch, where they were shown a parking address they do not have.
 *
 * So the rail is built from what a type CAN own, not from what this particular person happens to
 * have. A driver with no charger yet sees the charger row saying so — that is an answer. A manager
 * does not see it at all, because for a manager it is not a question. Types that can own nothing get
 * no rail, and the person card takes the full width instead of standing beside an empty box.
 */

/**
 * Who pays for this employee's charging, as a sentence rather than an enum.
 *
 * It used to be a coloured rectangle with COMPANY or EMPLOYEE inside it, which is a chip built for a
 * table cell where there is no room to write anything. In a card there is room, and a chip the same
 * shape as the account-type pill beside it read as a second, contradictory role.
 */
export const paymentModeLabel = (mode?: string) => {
    if (!mode) {
        return undefined;
    }

    return <FormattedMessage id={mode === 'COMPANY' ? 'paidByCompany' : 'paidByEmployee'}/>;
};

const asDate = (value?: number) => (value ? new Date(value).toLocaleDateString('fr-CH') : undefined);

const asAddress = (value?: {
    street?: string;
    houseNumber?: string;
    zipCode?: string;
    city?: string;
    country?: string;
}) => {
    if (!value) {
        return undefined;
    }

    return [
        [value.street, value.houseNumber].filter(Boolean).join(' '),
        [value.zipCode, value.city].filter(Boolean).join(' '),
        value.country,
    ].filter(Boolean).join(', ') || undefined;
};

const BACK_OFFICE = [
    UserTypeEnum.SUPER_ADMIN,
    UserTypeEnum.BUSINESS,
    UserTypeEnum.CLIENT_MANAGER,
    UserTypeEnum.SALES_MANAGER,
    UserTypeEnum.TECHNICAL_MANAGER,
    UserTypeEnum.REAL_ESTATE,
    UserTypeEnum.UNDEFINED,
];

/** The contract a driver is on right now, if any — the archived ones belong to the history below. */
const activeContract = (data: UserDTO) =>
    (data.contracts ?? []).find((item) => item.status !== ContractStatusEnum.ARCHIVED)
    ?? (data.contracts ?? [])[0];

export const isBackOfficeUser = (type?: UserTypeEnum) => BACK_OFFICE.includes(type as UserTypeEnum);

export const buildUserFacts = (data: UserDTO, location?: ReactNode): UserFact[] => {
    const registered = {
        label: 'registered',
        value: asDate(data.created),
        note: data.lastLogin ? `${asDate(data.lastLogin)}` : undefined,
    };

    if (data.userType === UserTypeEnum.EMPLOYEE) {
        return [
            {label: 'createNewCompanyTab', value: data.employee?.company?.name},
            {label: 'paymentModeColumnHeader', value: paymentModeLabel(data.employee?.consumptionPaymentMode)},
            {label: 'homeAddress', value: asAddress(data.liveAddress)},
            registered,
        ];
    }

    if (isBackOfficeUser(data.userType)) {
        // No addresses and no parking: a manager has neither, and printing two empty rows for
        // everyone in the back office was the old page treating them as drivers.
        return [
            {label: 'business', value: data.business?.name},
            {label: 'language', value: data.language},
            {label: 'lastLogin', value: asDate(data.lastLogin)},
            registered,
        ];
    }

    return [
        {label: 'location', value: location},
        {label: 'parkingAddress', value: asAddress(data.parkingAddress)},
        {label: 'homeAddress', value: asAddress(data.liveAddress)},
        registered,
    ];
};

export const buildLinkedRows = (data: UserDTO): LinkedRow[] => {
    const tags = data.ocppTags ?? [];
    const blocked = tags.filter((tag) => tag.blocked).length;
    const cards: LinkedRow = {
        label: 'rfidsTabHeader',
        icon: <CreditCardOutlined/>,
        value: tags.length ? String(tags.length) : undefined,
        note: blocked ? `${blocked} blocked` : undefined,
        emptyLabel: 'noCards',
    };

    const charger: LinkedRow = {
        label: 'charger',
        icon: <BoltOutlined/>,
        value: data.chargePoint?.name || data.chargePoint?.chargePointId,
        to: data.chargePoint
            ? AppRouteSpace([AppRouteEnum.app, AppRouteEnum.chargers, String(data.chargePoint.id)])
            : undefined,
        emptyLabel: 'noCharger',
    };

    /**
     * An employee's chargers and cards as counts, not as one named thing.
     *
     * A driver has the one charger assigned to them, so naming it is the useful answer. An employee
     * works at a company's site and carries the company's cards, so the question is how many - and
     * "No charger" was answering a question nobody asked about them.
     */
    const chargerCount: LinkedRow = {
        label: 'chargers',
        icon: <BoltOutlined/>,
        value: String(data.chargePoint ? 1 : 0),
        emptyLabel: 'noChargers',
    };

    const cardCount: LinkedRow = {
        label: 'rfidsTabHeader',
        icon: <CreditCardOutlined/>,
        value: String(tags.length),
        note: blocked ? `${blocked} blocked` : undefined,
        emptyLabel: 'noCards',
    };

    if (data.userType === UserTypeEnum.EMPLOYEE) {
        return [
            {
                label: 'createNewCompanyTab',
                icon: <BusinessOutlined/>,
                value: data.employee?.company?.name,
                emptyLabel: 'noCompany',
            },
            {
                label: 'paymentModeColumnHeader',
                icon: <PaymentsOutlined/>,
                value: data.employee?.consumptionPaymentMode,
                emptyLabel: 'notSet',
            },
            chargerCount,
            cardCount,
        ];
    }

    if (isBackOfficeUser(data.userType)) {
        // Nothing to link. The card takes the whole row rather than standing beside an empty rail.
        return [];
    }

    const contract = activeContract(data);

    return [
        charger,
        {
            label: 'tariffPlan',
            icon: <LocalOfferOutlined/>,
            value: contract?.tariff?.name,
            to: contract?.tariff?.id
                ? AppRouteSpace([AppRouteEnum.app, AppRouteEnum.tariffs, String(contract.tariff.id)])
                : undefined,
            emptyLabel: 'noPlan',
            accent: 'brand',
        },
        {
            label: 'activeContract',
            icon: <DescriptionOutlined/>,
            value: contract?.name,
            to: contract ? AppRouteSpace([AppRouteEnum.app, AppRouteEnum.contracts, String(contract.id)]) : undefined,
            emptyLabel: 'noContract',
        },
        cards,
    ];
};

